import { EDIT_PAGE_V2, RESUME_DRAFT_LEGACY } from '@confluence/named-routes';
import { BadStatusError } from '@confluence/network';

// eslint-disable-next-line no-restricted-imports
import type { ExperimentalMutations } from '../__types__/ExperimentalMutations';
import { fetchJSON } from '../fetchJSON';
import { MediaType } from '../MediaType';
import { safeResponseJSON } from '../safeJSONParse';
import { throwErrorWithResponse } from '../throwErrorWithResponse';

export const getDraftShareIdFromCreateContentResponse = (response: any) => {
	const context = response?._links?.context || '';
	const legacyUrl = response?._links?.editui || '';

	const legacyDraftRouteMatch = RESUME_DRAFT_LEGACY.match(context + legacyUrl);
	return legacyDraftRouteMatch?.query?.draftShareId || '';
};

export const experimentalCreateBlankFabricPage: ExperimentalMutations['experimentalCreateBlankFabricPage'] =
	async (_, { spaceKey, parentPage, title, isSpaceAliasFFEnabled = false }, { network }) => {
		const payload: any = {
			type: 'page',
			status: 'draft',
			metadata: {
				properties: {
					editor: {
						value: 'v2',
					},
				},
			},
			space: {
				key: spaceKey,
			},
		};

		if (parentPage) {
			payload.ancestors = [{ id: parentPage }];
		}

		if (title) {
			payload.title = title;
		}

		const expansions = `space,metadata.frontend.collabService`;

		const response = await fetchJSON(network, `/wiki/rest/api/content?expand=${expansions}`, {
			method: 'POST',
			headers: {
				'Content-Type': MediaType.JSON,
			},
			body: JSON.stringify(payload),
		}).catch(async (error) => {
			if (network.BadStatusError.isBadStatusError(error)) {
				const { response } = error;
				if (response) {
					// Try to convert the client/browser `error` to `backendError`,
					// because the latter is supposed to be more specific:
					const backendError = await safeResponseJSON(response);
					if (backendError) {
						// expected status codes
						const statusCode = response.status;
						// all 4xx status codes are expected and some 500s
						if (statusCode >= 400 && statusCode <= 500) {
							throw new BadStatusError(backendError.message, response);
						}
						throwErrorWithResponse(backendError, response);
					}
				}
			}
			throw error;
		});

		const contentId = response?.id;
		const spaceId = response?.space?.id;
		const subType = response?.subType;
		const draftUrl = EDIT_PAGE_V2.toUrl(
			{
				contentType: 'pages',
				spaceKey: isSpaceAliasFFEnabled ? response?.space?.alias : response?.space?.key,
				contentId,
			},
			{
				query: {
					draftShareId: getDraftShareIdFromCreateContentResponse(response),
				},
			},
		);

		return {
			contentId,
			spaceId,
			links: {
				draftUrl,
			},
			collabService: response?.metadata?.frontend?.collabService ?? undefined,
			subType,
		};
	};
